import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import type { WheelSegment } from './types';
import { resolveBrandFromSurveyObject, type ScratchCardBrand } from './ScratchCardCanvas';

/**
 * Pirinola — Xotski toton (Mexican spinning-top) game (Gate 5).
 *
 * v2 prize-face model (PRD_Xotski_Pirinola_Game_v2.md): the six Spanish command
 * faces are retired — each face now carries a piece of branded merch (the prize
 * IS the face). One tap spins the 3D top; it blurs, decelerates, runs a real
 * precession wobble, topples and settles slightly OFF-CENTER on the engine-chosen
 * face, which glows. The guest ALWAYS wins (no native pay-in / no-win face).
 *
 * Design law (game-engine §2): the brand is structural, not decorative. The brand
 * lives on the PLAYING SURFACE (logo), the LID emblem, the painted/LED BANDS, and
 * the GLOW/particles — never a corner banner. The toton BODY is a neutral material
 * (cream wood in Traditional, brushed alloy in Modern) by design.
 *
 * Which prize is NOT decided here. The wrapper picks the winner with the shared
 * prize engine (calculateWinningSegment, (6-rating)²) and passes it as `prize`;
 * this component choreographs the spin to land on its face and fires onRevealed.
 *
 * Ported faithfully from the working prototype:
 *   _reference/Pirinola Game -single file-.html  (piri-draw + piri-game modules).
 * Only the color SOURCE changed: every brand hue now derives from `brand` props.
 */

export type PirinolaEdition = 'traditional' | 'modern';
export type PirinolaPhase = 'idle' | 'spinning' | 'settling' | 'resolution' | 'winReveal';

// Re-export so the registry imports the resolver from one place.
export { resolveBrandFromSurveyObject };
export type { ScratchCardBrand };

export interface PirinolaProps {
  /** The prize the engine already chose. The guest always wins something. */
  prize: WheelSegment;
  /** Resolved 5-color brand. Use resolveBrandFromSurveyObject() for the 3-color shape. */
  brand: ScratchCardBrand;
  /** The six prize faces drawn on the toton. Defaults to the canonical merch set. */
  faces?: WheelSegment[];
  /** Material/stage skin — Traditional (turned wood) or Modern (brushed alloy + LED). */
  edition?: PirinolaEdition;
  /** Wordmark fallback for the surface logo when brand.logo is absent. */
  wordmark?: string;
  language?: 'en' | 'es';
  reducedMotion?: boolean;
  /** Force a visual phase for Storybook state stories (skips interaction). */
  demoPhase?: PirinolaPhase;
  /** Fires once when the toton settles on its face, with the won prize. */
  onRevealed?: (prize: WheelSegment) => void;
}

// ── Canonical six prize faces (drawn order MUST match the icon set below) ──
// Face i draws FACE_ICONS[i]; ratings drive the shared (6-rating)² weighting.
const FACE_ICONS = ['sunglasses', 'cap', 'tote', 'bottle', 'keychain', 'lanyard'] as const;
export const DEFAULT_FACES: WheelSegment[] = [
  { id: 'sunglasses', name: 'Sunglasses', nameTranslate: 'Lentes', emoji: '🕶️', rating: 5 },
  { id: 'cap', name: 'Cap', nameTranslate: 'Gorra', emoji: '🧢', rating: 4 },
  { id: 'tote', name: 'Tote Bag', nameTranslate: 'Bolsa', emoji: '👜', rating: 3 },
  { id: 'bottle', name: 'Bottle', nameTranslate: 'Botella', emoji: '🍶', rating: 2 },
  { id: 'keychain', name: 'Keychain', nameTranslate: 'Llavero', emoji: '🔑', rating: 2 },
  { id: 'lanyard', name: 'Lanyard', nameTranslate: 'Cordón', emoji: '🎗️', rating: 1 },
];

const T = {
  hint: { en: 'Tap the top', es: 'Toca la pirinola' },
  spin: { en: 'TAP TO SPIN', es: '¡GIRA!' },
  won: { en: 'You won', es: 'Ganaste' },
} as const;

/** Pick a legible text color (dark or light) for a brand-colored surface. */
function readableOn(hex: string): string {
  const [r, g, b] = hexRgb(hex);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6 ? '#1A1A1A' : '#FFFFFF';
}

// ── Color helpers ──
function hexRgb(hex: string): [number, number, number] {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}
function clampByte(c: number) { return Math.max(0, Math.min(255, Math.round(c))); }
function rgbHex(r: number, g: number, b: number) {
  return '#' + [r, g, b].map((c) => clampByte(c).toString(16).padStart(2, '0')).join('');
}
function mix(a: string, b: string, t: number) {
  const [r1, g1, b1] = hexRgb(a), [r2, g2, b2] = hexRgb(b);
  return rgbHex(r1 + (r2 - r1) * t, g1 + (g2 - g1) * t, b1 + (b2 - b1) * t);
}
function darken(hex: string, factor = 0.55) {
  const [r, g, b] = hexRgb(hex);
  return rgbHex(r * factor, g * factor, b * factor);
}
function rgba(hex: string, a: number) {
  const [r, g, b] = hexRgb(hex);
  return `rgba(${r},${g},${b},${a})`;
}
const isLightBg = (font: string) => font === '#000' || font === '#000000';

// ── Geometry (LOCKED from the prototype's piri-draw module) ──
const DCW = 340, DCH = 470, DCX = DCW / 2, DCY = DCH * 0.47;
const DSC = 76;
const D_TOP = 1.02, D_BOT = -0.70, D_TIP = -1.86;     // taller body + longer point
const D_PEGB = 0.10, D_PEGL = 0.62, D_PEGRW = 0.130;  // holder (taller + chunkier handle)
const D_VA0 = 0.46;
const NF = 6, DFARC = (Math.PI * 2) / NF;
// One painted/LED band near the top, one near the bottom — frees the face for the prize.
const BANDS_TOP = [{ v0: 0.105, v1: 0.180, k: 'top' as const }];
const BANDS_BOT = [{ v0: 0.820, v1: 0.895, k: 'bottom' as const }];

// ── Neutral material palettes (the BODY — wood vs alloy — is not a brand surface) ──
interface Material {
  bodyHi: string; body: string; body2: string; bodyDk: string; shade: string;
  metHi: string; met: string; metMd: string; metDk: string;
  grain: boolean; emissive: boolean; gloss: number; stage: 'candle' | 'holo';
}
const MATS: Record<PirinolaEdition, Material> = {
  traditional: {
    bodyHi: '#F8F1E1', body: '#ECE1C6', body2: '#E7DAC0', bodyDk: '#CDBE9D', shade: '#5E4A2C',
    metHi: '#FCEAA6', met: '#E4B23E', metMd: '#BE852A', metDk: '#7E5616',
    grain: true, emissive: false, gloss: 0.0, stage: 'candle',
  },
  modern: {
    bodyHi: '#FCFEFF', body: '#DCE6EE', body2: '#C5D2DE', bodyDk: '#8A99A9', shade: '#1E2C3A',
    metHi: '#EBFCFF', met: '#5FE0F5', metMd: '#1E9CC4', metDk: '#0B4C66',
    grain: false, emissive: true, gloss: 0.55, stage: 'holo',
  },
};

/** Brand-derived band + glow + emblem colors (PRD §8.2: bands/glow are brand surfaces). */
interface BrandBand { top: string; bottom: string; glow: string; markCol: string; mark: string; }
function deriveBand(brand: ScratchCardBrand, edition: PirinolaEdition, wordmark?: string): BrandBand {
  // Modern lifts the hues toward emissive neon; Traditional keeps them painted.
  const lift = edition === 'modern' ? (h: string) => mix(h, '#FFFFFF', 0.18) : (h: string) => h;
  return {
    top: lift(brand.secondary),
    bottom: lift(brand.accent),
    glow: lift(brand.accent),
    markCol: edition === 'modern' ? mix(brand.primary, '#FFFFFF', 0.25) : brand.primary,
    mark: (wordmark || 'B').trim().charAt(0).toUpperCase() || 'B',
  };
}

// ── 3D → screen projection (Z-lean wobble · X-fall · view-tilt) ──
interface Pt { sx: number; sy: number; z: number; }
function dview(wx: number, wy: number, wz: number, wob: number, fallA: number, va: number): Pt {
  const cW = Math.cos(wob), sW = Math.sin(wob);
  const x1 = wx * cW - wy * sW, y1 = wx * sW + wy * cW, z1 = wz;
  const cF = Math.cos(fallA), sF = Math.sin(fallA);
  const y2 = y1 * cF + z1 * sF, z2 = -y1 * sF + z1 * cF;
  const cV = Math.cos(va), sV = Math.sin(va);
  const y3 = y2 * cV - z2 * sV, z3 = y2 * sV + z2 * cV;
  return { sx: DCX + x1 * DSC, sy: DCY - y3 * DSC, z: z3 };
}
function dfaceZ(dx: number, dy: number, dz: number, wob: number, fallA: number, va: number): number {
  const cW = Math.cos(wob), sW = Math.sin(wob);
  const y1 = dx * sW + dy * cW, z1 = dz;
  const cF = Math.cos(fallA), sF = Math.sin(fallA);
  const y2 = y1 * cF + z1 * sF, z2 = -y1 * sF + z1 * cF;
  return y2 * Math.sin(va) + z2 * Math.cos(va);
}
type S2 = { sx: number; sy: number };
const lp = (p: S2, q: S2, t: number): S2 => ({ sx: p.sx + (q.sx - p.sx) * t, sy: p.sy + (q.sy - p.sy) * t });
function fp(tl: S2, tr: S2, br: S2, bl: S2, u: number, v: number): S2 {
  const L = lp(tl, bl, v), R = lp(tr, br, v);
  return lp(L, R, u);
}

// ── Prize line-icons drawn on each face ──
function drawPrize(ctx: CanvasRenderingContext2D, id: string, cx: number, cy: number, s: number, col: string, glow: string | null, lw?: number, glowBlur?: number) {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.strokeStyle = col; ctx.fillStyle = col;
  ctx.lineWidth = lw ?? Math.max(1.1, s * 0.07); ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  if (glow) { ctx.shadowColor = glow; ctx.shadowBlur = glowBlur ?? s * 0.5; }
  const u = s;
  if (id === 'sunglasses') {
    ctx.beginPath(); ctx.ellipse(-0.52 * u, 0.05 * u, 0.42 * u, 0.32 * u, 0, 0, Math.PI * 2); ctx.stroke();
    ctx.beginPath(); ctx.ellipse(0.52 * u, 0.05 * u, 0.42 * u, 0.32 * u, 0, 0, Math.PI * 2); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(-0.1 * u, -0.02 * u); ctx.quadraticCurveTo(0, -0.12 * u, 0.1 * u, -0.02 * u); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(-0.94 * u, -0.06 * u); ctx.lineTo(-1.15 * u, -0.2 * u); ctx.moveTo(0.94 * u, -0.06 * u); ctx.lineTo(1.15 * u, -0.2 * u); ctx.stroke();
  } else if (id === 'cap') {
    ctx.beginPath(); ctx.arc(0, 0.08 * u, 0.7 * u, Math.PI, 0); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(-0.7 * u, 0.08 * u); ctx.quadraticCurveTo(-1.15 * u, 0.22 * u, -0.95 * u, 0.4 * u); ctx.quadraticCurveTo(-0.5 * u, 0.32 * u, -0.2 * u, 0.3 * u); ctx.stroke();
    ctx.beginPath(); ctx.arc(0, -0.18 * u, 0.07 * u, 0, Math.PI * 2); ctx.fill();
  } else if (id === 'tote') {
    ctx.beginPath(); ctx.moveTo(-0.55 * u, -0.2 * u); ctx.lineTo(-0.72 * u, 0.55 * u); ctx.lineTo(0.72 * u, 0.55 * u); ctx.lineTo(0.55 * u, -0.2 * u); ctx.closePath(); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(-0.32 * u, -0.2 * u); ctx.quadraticCurveTo(-0.32 * u, -0.62 * u, 0, -0.62 * u); ctx.quadraticCurveTo(0.32 * u, -0.62 * u, 0.32 * u, -0.2 * u); ctx.stroke();
  } else if (id === 'bottle') {
    ctx.beginPath(); ctx.moveTo(-0.12 * u, -0.62 * u); ctx.lineTo(0.12 * u, -0.62 * u); ctx.lineTo(0.12 * u, -0.42 * u);
    ctx.quadraticCurveTo(0.34 * u, -0.3 * u, 0.34 * u, -0.02 * u); ctx.lineTo(0.34 * u, 0.5 * u); ctx.quadraticCurveTo(0.34 * u, 0.6 * u, 0.24 * u, 0.6 * u);
    ctx.lineTo(-0.24 * u, 0.6 * u); ctx.quadraticCurveTo(-0.34 * u, 0.6 * u, -0.34 * u, 0.5 * u); ctx.lineTo(-0.34 * u, -0.02 * u);
    ctx.quadraticCurveTo(-0.34 * u, -0.3 * u, -0.12 * u, -0.42 * u); ctx.closePath(); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(-0.34 * u, 0.16 * u); ctx.lineTo(0.34 * u, 0.16 * u); ctx.stroke();
  } else if (id === 'keychain') {
    ctx.beginPath(); ctx.arc(-0.34 * u, -0.28 * u, 0.34 * u, 0, Math.PI * 2); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(-0.1 * u, -0.04 * u); ctx.lineTo(0.5 * u, 0.56 * u); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(0.5 * u, 0.56 * u); ctx.lineTo(0.66 * u, 0.4 * u); ctx.moveTo(0.36 * u, 0.42 * u); ctx.lineTo(0.5 * u, 0.28 * u); ctx.stroke();
  } else if (id === 'lanyard') {
    ctx.beginPath(); ctx.moveTo(0, -0.62 * u); ctx.quadraticCurveTo(0.5 * u, -0.62 * u, 0.5 * u, -0.32 * u); ctx.quadraticCurveTo(0.5 * u, -0.05 * u, 0, 0.05 * u);
    ctx.quadraticCurveTo(-0.5 * u, -0.05 * u, -0.5 * u, -0.32 * u); ctx.quadraticCurveTo(-0.5 * u, -0.62 * u, 0, -0.62 * u); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(0, 0.05 * u); ctx.lineTo(0, 0.24 * u); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(-0.3 * u, 0.24 * u); ctx.lineTo(0.3 * u, 0.24 * u); ctx.lineTo(0.3 * u, 0.6 * u); ctx.lineTo(-0.3 * u, 0.6 * u); ctx.closePath(); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(-0.16 * u, 0.4 * u); ctx.lineTo(0.16 * u, 0.4 * u); ctx.stroke();
  } else {
    // Unknown inventory item → emoji glyph fallback.
    ctx.font = `${s * 1.3}px serif`; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(id, 0, 0);
  }
  ctx.restore();
}

// ── Render state fed to renderPirinola each frame ──
interface RenderState {
  sp: number; wob: number; fall: number; va: number;
  reveal: number; now: number;
  /** Normalized angular speed 0..1 — drives the intensity of the spin motion FX. */
  spinSpeed: number;
  /** Screen-space travel offset — the top wanders over the (fixed) surface. */
  tx: number; ty: number;
  edition: PirinolaEdition; brand: ScratchCardBrand; band: BrandBand;
  lang: 'en' | 'es'; embers: Ember[]; showReveal: boolean;
  faces: WheelSegment[]; logoImg: HTMLImageElement | null; wordmark: string;
}
interface Ember { x: number; y: number; vy: number; r: number; a: number; ph: number }

// Fit the brand logo into a rect, preserving aspect ratio, centered.
function drawLogoFitted(ctx: CanvasRenderingContext2D, img: HTMLImageElement | null, cx: number, cy: number, maxW: number, maxH: number, fallbackText: string, fill: string, glow: string | null) {
  if (img && img.complete && img.naturalWidth) {
    const ratio = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight);
    const dw = img.naturalWidth * ratio, dh = img.naturalHeight * ratio;
    if (glow) { ctx.save(); ctx.shadowColor = glow; ctx.shadowBlur = 12; }
    ctx.drawImage(img, cx - dw / 2, cy - dh / 2, dw, dh);
    if (glow) ctx.restore();
  } else {
    ctx.font = `800 ${fallbackText.length > 6 ? 26 : 32}px 'DM Sans',sans-serif`;
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    if (glow) { ctx.shadowColor = glow; ctx.shadowBlur = 14; }
    ctx.fillStyle = fill; ctx.fillText(fallbackText.toUpperCase(), cx, cy);
    ctx.shadowBlur = 0;
  }
}

// ── Branded playing surface (the disc the toton spins on) ──
function drawStage(ctx: CanvasRenderingContext2D, S: RenderState) {
  const { edition, brand, band, now, embers, logoImg, wordmark } = S;
  const M = MATS[edition];
  const cx = DCX, cy = DCY + 128, rx = 152, ry = 34;
  // backdrop — neutral dark theater, tinted toward the brand primary so light/dark brands read.
  const tintDark = darken(brand.primary, 0.16);
  const bgG = ctx.createRadialGradient(cx, DCY - 30, 20, cx, DCY + 60, 340);
  if (M.stage === 'candle') {
    bgG.addColorStop(0, mix('#2A161B', tintDark, 0.4)); bgG.addColorStop(0.55, mix('#180D11', tintDark, 0.3)); bgG.addColorStop(1, '#0A0608');
  } else {
    bgG.addColorStop(0, mix('#13202C', tintDark, 0.4)); bgG.addColorStop(0.55, mix('#0B141D', tintDark, 0.3)); bgG.addColorStop(1, '#05080C');
  }
  ctx.fillStyle = bgG; ctx.fillRect(0, 0, DCW, DCH);
  // ambient glow behind the top
  const gl = ctx.createRadialGradient(cx, DCY - 20, 8, cx, DCY - 20, 200);
  gl.addColorStop(0, rgba(band.glow, M.emissive ? 0.15 : 0.20)); gl.addColorStop(1, 'transparent');
  ctx.fillStyle = gl; ctx.fillRect(0, 0, DCW, DCH);

  // soft contact shadow under the disc
  ctx.save();
  ctx.beginPath(); ctx.ellipse(cx, cy + 9, rx * 1.02, ry * 1.12, 0, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(0,0,0,.5)'; ctx.filter = 'blur(9px)'; ctx.fill(); ctx.filter = 'none';
  ctx.restore();

  // disc face
  ctx.beginPath(); ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
  const dg = ctx.createRadialGradient(cx, cy - ry * 0.35, 4, cx, cy, rx);
  if (M.emissive) { dg.addColorStop(0, mix('#16293A', band.glow, 0.22)); dg.addColorStop(0.7, '#0E1C28'); dg.addColorStop(1, '#070E15'); }
  else { dg.addColorStop(0, '#3C2C1F'); dg.addColorStop(0.68, '#251810'); dg.addColorStop(1, '#150C07'); }
  ctx.fillStyle = dg; ctx.fill();

  // top sheen
  ctx.save(); ctx.beginPath(); ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2); ctx.clip();
  const sh = ctx.createLinearGradient(0, cy - ry, 0, cy + ry);
  sh.addColorStop(0, M.emissive ? rgba(band.glow, 0.10) : 'rgba(255,240,210,.07)'); sh.addColorStop(0.5, 'transparent');
  ctx.fillStyle = sh; ctx.fillRect(cx - rx, cy - ry, rx * 2, ry * 2); ctx.restore();

  // rim
  ctx.lineWidth = 2.4; ctx.strokeStyle = M.emissive ? rgba(band.glow, 0.6) : rgba(M.met, 0.5);
  ctx.beginPath(); ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
  if (M.emissive) { ctx.shadowColor = band.glow; ctx.shadowBlur = 16; } ctx.stroke(); ctx.shadowBlur = 0;
  ctx.lineWidth = 1; ctx.strokeStyle = M.emissive ? rgba(band.glow, 0.22) : rgba(M.met, 0.20);
  ctx.beginPath(); ctx.ellipse(cx, cy, rx * 0.72, ry * 0.72, 0, 0, Math.PI * 2); ctx.stroke();

  // surface decoration: rotating ticks (modern) or marigold petals (traditional)
  if (M.emissive) {
    ctx.save(); ctx.translate(cx, cy); ctx.scale(1, ry / rx);
    ctx.strokeStyle = rgba(band.glow, 0.5); ctx.lineWidth = 2;
    for (let i = 0; i < 24; i++) {
      const a = now * 0.5 + i * Math.PI / 12, r0 = rx * 0.84, r1 = rx * 0.94;
      ctx.beginPath(); ctx.moveTo(Math.cos(a) * r0, Math.sin(a) * r0); ctx.lineTo(Math.cos(a) * r1, Math.sin(a) * r1); ctx.stroke();
    }
    ctx.restore();
  } else {
    ctx.save(); ctx.translate(cx, cy); ctx.scale(1, ry / rx);
    for (let i = 0; i < 7; i++) {
      const a = i * 2.4 + 1, r = rx * (0.5 + 0.42 * ((i * 0.27) % 1)); const px = Math.cos(a) * r, py = Math.sin(a) * r;
      ctx.fillStyle = rgba(band.glow, 0.5); ctx.beginPath(); ctx.ellipse(px, py, 4, 2.4, a, 0, Math.PI * 2); ctx.fill();
    }
    ctx.restore();
  }

  // BRAND LOGO printed on the surface (front half, laid flat in perspective)
  ctx.save();
  ctx.translate(cx, cy + ry * 0.30);
  ctx.scale(1, 0.40);
  drawLogoFitted(ctx, logoImg, 0, 0, 196, 64, wordmark, M.emissive ? rgba(band.glow, 0.92) : rgba(M.met, 0.95), M.emissive ? band.glow : null);
  ctx.restore();

  // ambient particles above the surface
  embers.forEach((p) => {
    const a = p.a * (0.55 + 0.45 * Math.sin(now * 2.2 + p.ph));
    let py = p.y;
    if (M.emissive) { py = cy - ((((p.y - now * 22 * p.vy) % 240) + 240) % 240) * 0.9 - 2; }
    ctx.globalAlpha = a; ctx.fillStyle = band.glow;
    ctx.beginPath(); ctx.arc(p.x, py, p.r * (M.emissive ? 0.7 : 1), 0, Math.PI * 2); ctx.fill();
  });
  ctx.globalAlpha = 1;
}

// ════════════════════════════════════════════════════════════════════════
//  renderPirinola — the reusable drawing core. Both the static demoPhase
//  poses and the live physics loop feed it a state object each frame.
// ════════════════════════════════════════════════════════════════════════
function renderPirinola(ctx: CanvasRenderingContext2D, S: RenderState) {
  const { sp, wob, fall, va, reveal, now, edition, band, lang, faces } = S;
  const M = MATS[edition];
  // The top TRAVELS across the surface (tx,ty) while it spins — the disc stays put.
  const tx = S.tx, ty = S.ty;
  const P = (x: number, y: number, z: number) => {
    const p = dview(x, y, z, wob, fall, va);
    return { sx: p.sx + tx, sy: p.sy + ty, z: p.z };
  };

  ctx.clearRect(0, 0, DCW, DCH);
  drawStage(ctx, S);

  // ring vertices
  const topV: Pt[] = [], botV: Pt[] = [];
  for (let i = 0; i < NF; i++) {
    const a = sp + i * DFARC;
    topV.push(P(Math.cos(a), D_TOP, Math.sin(a)));
    botV.push(P(Math.cos(a), D_BOT, Math.sin(a)));
  }
  const apex = P(0, D_TIP, 0);
  const pegBase = P(0, D_TOP + D_PEGB, 0), pegTop = P(0, D_TOP + D_PEGB + D_PEGL, 0);

  // ground shadow + bounce
  let maxSy = -1e9, cxS = 0;
  [...topV, ...botV, apex].forEach((p) => { if (p.sy > maxSy) maxSy = p.sy; cxS += p.sx; }); cxS /= (NF * 2 + 1);
  const shY = maxSy + (fall > 0.7 ? 6 : 12), shW = fall > 0.2 ? 56 : 46;
  const sg = ctx.createRadialGradient(cxS, shY, 0, cxS, shY, shW);
  sg.addColorStop(0, 'rgba(0,0,0,.5)'); sg.addColorStop(1, 'transparent');
  ctx.beginPath(); ctx.ellipse(cxS, shY, shW, 13, 0, 0, Math.PI * 2); ctx.fillStyle = sg; ctx.fill();
  const bo = ctx.createRadialGradient(cxS, shY - 2, 0, cxS, shY - 2, shW * 0.8);
  bo.addColorStop(0, rgba(band.glow, 0.12)); bo.addColorStop(1, 'transparent');
  ctx.beginPath(); ctx.ellipse(cxS, shY - 2, shW * 0.8, 10, 0, 0, Math.PI * 2); ctx.fillStyle = bo; ctx.fill();

  const capFront = dfaceZ(0, 1, 0, wob, fall, va);
  // A lathe-turned handle: seated metal collar → tapered dowel with a waist →
  // rounded finial knob, with cylindrical shading and a specular highlight.
  const drawPeg = () => {
    const dx = pegTop.sx - pegBase.sx, dy = pegTop.sy - pegBase.sy, L = Math.hypot(dx, dy) || 1;
    const ux = dx / L, uy = dy / L;          // along-axis unit (base → top)
    const nx = -uy, ny = ux;                  // normal unit (across the shaft)
    const hw = D_PEGRW * DSC;
    const ang = Math.atan2(dy, dx);
    const ax = (f: number) => ({ x: pegBase.sx + dx * f, y: pegBase.sy + dy * f });

    // seated metal collar/ferrule at the base (drawn first so the shaft rises from it)
    ctx.beginPath(); ctx.ellipse(pegBase.sx, pegBase.sy, hw * 1.5, hw * 0.62, ang, 0, Math.PI * 2);
    ctx.fillStyle = M.met; if (M.emissive) { ctx.shadowColor = band.glow; ctx.shadowBlur = 8; }
    ctx.fill(); ctx.shadowBlur = 0; ctx.strokeStyle = rgba(M.metDk, 0.7); ctx.lineWidth = 0.8; ctx.stroke();
    ctx.beginPath(); ctx.ellipse(pegBase.sx, pegBase.sy - hw * 0.12, hw * 1.18, hw * 0.42, ang, 0, Math.PI * 2);
    ctx.fillStyle = rgba(M.metHi, 0.6); ctx.fill();

    // turned dowel silhouette — [fraction along axis, half-width factor]
    const prof: [number, number][] = [[0.0, 1.04], [0.16, 1.12], [0.46, 0.80], [0.70, 0.50], [0.78, 0.44]];
    ctx.beginPath();
    prof.forEach(([f, wf], i) => { const p = ax(f), x = p.x + nx * hw * wf, y = p.y + ny * hw * wf; i ? ctx.lineTo(x, y) : ctx.moveTo(x, y); });
    for (let i = prof.length - 1; i >= 0; i--) { const [f, wf] = prof[i], p = ax(f); ctx.lineTo(p.x - nx * hw * wf, p.y - ny * hw * wf); }
    ctx.closePath();
    const g = ctx.createLinearGradient(pegBase.sx + nx * hw, pegBase.sy + ny * hw, pegBase.sx - nx * hw, pegBase.sy - ny * hw);
    if (M.emissive) { g.addColorStop(0, M.metHi); g.addColorStop(0.38, M.bodyHi); g.addColorStop(0.55, M.body); g.addColorStop(0.78, M.met); g.addColorStop(1, M.metDk); }
    else { g.addColorStop(0, M.bodyHi); g.addColorStop(0.45, M.body2); g.addColorStop(0.72, mix(M.body, M.shade, 0.28)); g.addColorStop(1, mix(M.bodyDk, M.shade, 0.42)); }
    if (M.emissive) { ctx.shadowColor = band.glow; ctx.shadowBlur = 7; }
    ctx.fillStyle = g; ctx.fill(); ctx.shadowBlur = 0;
    ctx.strokeStyle = rgba(M.shade, 0.4); ctx.lineWidth = 0.8; ctx.stroke();

    // specular highlight running up the shaft
    const h0 = ax(0.08), h1 = ax(0.68);
    ctx.beginPath(); ctx.lineCap = 'round'; ctx.lineWidth = hw * 0.34;
    ctx.moveTo(h0.x + nx * hw * 0.5, h0.y + ny * hw * 0.5); ctx.lineTo(h1.x + nx * hw * 0.3, h1.y + ny * hw * 0.3);
    ctx.strokeStyle = M.emissive ? 'rgba(255,255,255,.7)' : 'rgba(255,255,255,.5)'; ctx.stroke();

    // rounded finial knob at the top
    const fc = ax(0.84), fr = hw * 1.28;
    const fg = ctx.createRadialGradient(fc.x - fr * 0.35, fc.y - fr * 0.42, fr * 0.1, fc.x, fc.y, fr);
    if (M.emissive) { fg.addColorStop(0, '#FFFFFF'); fg.addColorStop(0.5, M.metHi); fg.addColorStop(1, M.met); ctx.shadowColor = band.glow; ctx.shadowBlur = 12; }
    else { fg.addColorStop(0, '#FFFFFF'); fg.addColorStop(0.45, M.bodyHi); fg.addColorStop(1, mix(M.body2, M.shade, 0.3)); }
    ctx.beginPath(); ctx.arc(fc.x, fc.y, fr, 0, Math.PI * 2); ctx.fillStyle = fg; ctx.fill(); ctx.shadowBlur = 0;
    ctx.strokeStyle = rgba(M.shade, 0.4); ctx.lineWidth = 0.7; ctx.stroke();
    ctx.beginPath(); ctx.arc(fc.x - fr * 0.32, fc.y - fr * 0.36, fr * 0.26, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255,255,255,.85)'; ctx.fill();
  };

  const drawLid = (verts: Pt[]) => {
    ctx.beginPath(); verts.forEach((p, i) => i ? ctx.lineTo(p.sx, p.sy) : ctx.moveTo(p.sx, p.sy)); ctx.closePath();
    const cx = verts.reduce((s, p) => s + p.sx, 0) / NF, cy = verts.reduce((s, p) => s + p.sy, 0) / NF;
    const wG = ctx.createRadialGradient(cx - 6, cy - 7, 1, cx, cy, DSC * 0.95);
    wG.addColorStop(0, M.bodyHi); wG.addColorStop(1, mix(M.body, M.shade, 0.22));
    ctx.fillStyle = wG; ctx.fill(); ctx.strokeStyle = rgba(M.shade, 0.55); ctx.lineWidth = 1.1; ctx.stroke();
    // Grooves + emblem + mark are drawn ON the lid plane (perspective basis), so the
    // gold "circle on top" tilts and skews with the top instead of staying level.
    const pc = P(0, D_TOP, 0), pex = P(1, D_TOP, 0), pez = P(0, D_TOP, 1);
    const ex = { x: pex.sx - pc.sx, y: pex.sy - pc.sy };  // lid in-plane x basis (1 world unit)
    const ez = { x: pez.sx - pc.sx, y: pez.sy - pc.sy };  // lid in-plane z basis (1 world unit)
    const planeScale = Math.hypot(ex.x, ex.y) || 1;
    ctx.save();
    ctx.beginPath(); verts.forEach((p, i) => i ? ctx.lineTo(p.sx, p.sy) : ctx.moveTo(p.sx, p.sy)); ctx.closePath(); ctx.clip();
    ctx.transform(ex.x, ex.y, ez.x, ez.y, pc.sx, pc.sy); // now 1 unit = 1 world unit on the lid
    // concentric turned-wood grooves
    ctx.strokeStyle = M.emissive ? rgba(band.glow, 0.22) : rgba(M.shade, 0.16); ctx.lineWidth = 0.9 / planeScale;
    for (let k = 1; k <= 3; k++) { ctx.beginPath(); ctx.arc(0, 0, 0.29 * k, 0, Math.PI * 2); ctx.stroke(); }
    // Metal emblem boss — the turned seat the handle rises from (no letter; the
    // brand wordmark already reads on the playing surface below).
    const er = 0.40;
    const g = ctx.createRadialGradient(-er * 0.3, -er * 0.4, 0.01, 0, 0, er);
    if (M.emissive) { g.addColorStop(0, '#FFFFFF'); g.addColorStop(0.4, M.metHi); g.addColorStop(0.8, M.met); g.addColorStop(1, M.metDk); }
    else { g.addColorStop(0, '#FFF6D6'); g.addColorStop(0.35, M.metHi); g.addColorStop(0.7, M.met); g.addColorStop(1, M.metDk); }
    ctx.beginPath(); ctx.arc(0, 0, er, 0, Math.PI * 2);
    if (M.emissive) { ctx.shadowColor = band.glow; ctx.shadowBlur = 12; }
    ctx.fillStyle = g; ctx.fill(); ctx.shadowBlur = 0;
    // brand-colored accent ring around the boss (keeps brand on the lid, subtly)
    ctx.strokeStyle = rgba(band.glow, M.emissive ? 0.75 : 0.5); ctx.lineWidth = 1.6 / planeScale;
    ctx.beginPath(); ctx.arc(0, 0, er, 0, Math.PI * 2); ctx.stroke();
    // recessed seat hole the dowel inserts into
    const sr = 0.18;
    const sg = ctx.createRadialGradient(0, 0, sr * 0.2, 0, 0, sr);
    sg.addColorStop(0, M.emissive ? mix(M.metDk, '#000000', 0.25) : mix(M.shade, '#000000', 0.15));
    sg.addColorStop(1, M.emissive ? M.metMd : mix(M.met, M.shade, 0.4));
    ctx.beginPath(); ctx.arc(0, 0, sr, 0, Math.PI * 2); ctx.fillStyle = sg; ctx.fill();
    ctx.restore();
  };

  // SOLID
  if (capFront <= 0) drawPeg();
  interface FaceItem { type: 'face' | 'cone'; i: number; quad?: Pt[]; tri?: Pt[]; z: number; nz: number }
  const items: FaceItem[] = [];
  for (let i = 0; i < NF; i++) {
    const j = (i + 1) % NF, aMid = sp + (i + 0.5) * DFARC;
    const nz = dfaceZ(Math.cos(aMid), 0, Math.sin(aMid), wob, fall, va);
    if (nz > -0.06) {
      items.push({ type: 'face', i, quad: [topV[i], topV[j], botV[j], botV[i]], z: (topV[i].z + topV[j].z + botV[j].z + botV[i].z) / 4, nz });
      items.push({ type: 'cone', i, tri: [botV[i], botV[j], apex], z: (botV[i].z + botV[j].z + apex.z) / 3, nz });
    }
  }
  items.sort((a, b) => a.z - b.z);

  items.forEach((it) => {
    if (it.type === 'cone') {
      const [a, b, c] = it.tri!, nz = it.nz, light = Math.max(0.32, 0.46 + 0.5 * nz);
      ctx.beginPath(); ctx.moveTo(a.sx, a.sy); ctx.lineTo(b.sx, b.sy); ctx.lineTo(c.sx, c.sy); ctx.closePath();
      const cG = ctx.createLinearGradient(a.sx, a.sy, c.sx, c.sy);
      cG.addColorStop(0, mix(M.body, M.shade, 1 - light)); cG.addColorStop(0.72, mix(M.bodyDk, M.shade, 1 - light * 0.6)); cG.addColorStop(1, mix(M.bodyDk, M.shade, 0.55));
      ctx.fillStyle = cG; ctx.fill(); ctx.strokeStyle = rgba(M.shade, 0.3); ctx.lineWidth = 0.7; ctx.stroke();
      return;
    }
    // Prefer the face's own canonical line-icon (default merch set); for real
    // inventory whose id isn't a known icon, draw its emoji so the face matches
    // the actual prize (not a wrong canonical icon). Fall back to the slot icon.
    const faceSeg = faces[it.i % NF];
    const faceId = faceSeg && (FACE_ICONS as readonly string[]).includes(faceSeg.id as string)
      ? (faceSeg.id as string)
      : (faceSeg?.emoji || FACE_ICONS[it.i % NF]);
    const [tl, tr, br, bl] = it.quad!, nz = it.nz;
    const light = Math.max(0.4, 0.5 + 0.5 * nz);
    ctx.beginPath(); ctx.moveTo(tl.sx, tl.sy); ctx.lineTo(tr.sx, tr.sy); ctx.lineTo(br.sx, br.sy); ctx.lineTo(bl.sx, bl.sy); ctx.closePath();
    const fG = ctx.createLinearGradient((tl.sx + tr.sx) / 2, (tl.sy + tr.sy) / 2, (bl.sx + br.sx) / 2, (bl.sy + br.sy) / 2);
    fG.addColorStop(0, mix(M.bodyHi, M.shade, 1 - light)); fG.addColorStop(1, mix(M.body2, M.shade, 1 - light * 0.8));
    ctx.fillStyle = fG; ctx.fill();
    if (M.grain) {
      ctx.save(); ctx.clip(); ctx.globalAlpha = 0.08 * light; ctx.strokeStyle = M.shade; ctx.lineWidth = 1;
      for (let g = 1; g < 6; g++) { const tt = g / 6; ctx.beginPath(); ctx.moveTo(tl.sx + (tr.sx - tl.sx) * tt, tl.sy + (tr.sy - tl.sy) * tt); ctx.lineTo(bl.sx + (br.sx - bl.sx) * tt, bl.sy + (br.sy - bl.sy) * tt); ctx.stroke(); }
      ctx.restore();
    } else {
      ctx.save(); ctx.clip();
      const shg = ctx.createLinearGradient(tl.sx, tl.sy, tr.sx, tr.sy);
      shg.addColorStop(0, 'rgba(255,255,255,0)'); shg.addColorStop(0.5, `rgba(255,255,255,${0.16 * light})`); shg.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = shg; ctx.fillRect(0, 0, DCW, DCH); ctx.restore();
    }
    ctx.save(); ctx.beginPath(); ctx.moveTo(tl.sx, tl.sy); ctx.lineTo(tr.sx, tr.sy); ctx.lineTo(br.sx, br.sy); ctx.lineTo(bl.sx, bl.sy); ctx.closePath(); ctx.clip();
    const bandFill = (v0: number, v1: number, k: 'top' | 'bottom') => {
      const col = k === 'top' ? band.top : band.bottom;
      const a0 = lp(tl, bl, v0), b0 = lp(tr, br, v0), a1 = lp(tl, bl, v1), b1 = lp(tr, br, v1);
      ctx.beginPath(); ctx.moveTo(a0.sx, a0.sy); ctx.lineTo(b0.sx, b0.sy); ctx.lineTo(b1.sx, b1.sy); ctx.lineTo(a1.sx, a1.sy); ctx.closePath();
      if (M.emissive) {
        const bg2 = ctx.createLinearGradient(a0.sx, a0.sy, a1.sx, a1.sy);
        bg2.addColorStop(0, mix(col, '#FFFFFF', 0.45)); bg2.addColorStop(0.5, col); bg2.addColorStop(1, mix(col, '#000000', 0.15));
        ctx.shadowColor = col; ctx.shadowBlur = 9 * light; ctx.fillStyle = bg2; ctx.fill(); ctx.shadowBlur = 0;
      } else {
        const bg2 = ctx.createLinearGradient(a0.sx, a0.sy, a1.sx, a1.sy);
        bg2.addColorStop(0, mix(col, '#FFFFFF', 0.18 * light)); bg2.addColorStop(0.5, mix(col, M.shade, 1 - light)); bg2.addColorStop(1, mix(col, '#000000', 0.25));
        ctx.fillStyle = bg2; ctx.fill();
        ctx.strokeStyle = `rgba(255,255,255,${0.28 * light})`; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(a0.sx, a0.sy + 1); ctx.lineTo(b0.sx, b0.sy + 1); ctx.stroke();
      }
    };
    BANDS_TOP.forEach((b) => bandFill(b.v0, b.v1, b.k));
    BANDS_BOT.forEach((b) => bandFill(b.v0, b.v1, b.k));
    ctx.restore();
    if (nz > 0.12) {
      // Draw the prize icon ON the face plane (perspective-mapped) so it skews and
      // tilts with the face — not pasted flat/upright in screen space.
      const c = fp(tl, tr, br, bl, 0.5, 0.47);
      const cR = fp(tl, tr, br, bl, 1, 0.47), cL = fp(tl, tr, br, bl, 0, 0.47);
      const cT = fp(tl, tr, br, bl, 0.5, 0.12), cB = fp(tl, tr, br, bl, 0.5, 0.82);
      const axv = (cR.sx - cL.sx) / 2, ayv = (cR.sy - cL.sy) / 2;   // face x-basis (half width)
      const bxv = (cB.sx - cT.sx) / 2, byv = (cB.sy - cT.sy) / 2;   // face y-basis (half height, down)
      const k = 0.46;                                              // icon size relative to the face
      const scale = Math.hypot(axv, ayv) || 1;
      const ia = Math.min(1, Math.max(0, (nz - 0.12) * 2.2));
      ctx.save();
      ctx.globalAlpha = ia;
      ctx.transform(axv * k, ayv * k, bxv * k, byv * k, c.sx, c.sy);
      drawPrize(ctx, faceId, 0, 0, 1, M.emissive ? M.metHi : mix('#3A2A18', M.shade, 0.2), M.emissive ? band.glow : null, 1.8 / (scale * k), 8);
      ctx.restore();
      ctx.globalAlpha = 1;
    }
    if (reveal === it.i) {
      const c = fp(tl, tr, br, bl, 0.5, 0.47), pulse = 0.3 + 0.2 * Math.sin(now * 5);
      const rg = ctx.createRadialGradient(c.sx, c.sy, 0, c.sx, c.sy, DSC * 0.8);
      rg.addColorStop(0, rgba(band.glow, pulse * 0.7)); rg.addColorStop(1, 'transparent');
      ctx.beginPath(); ctx.moveTo(tl.sx, tl.sy); ctx.lineTo(tr.sx, tr.sy); ctx.lineTo(br.sx, br.sy); ctx.lineTo(bl.sx, bl.sy); ctx.closePath();
      ctx.save(); ctx.clip(); ctx.fillStyle = rg; ctx.fillRect(0, 0, DCW, DCH); ctx.restore();
    }
    {
      const lx = tl.sx + (tr.sx - tl.sx) * 0.28 + (bl.sx - tl.sx) * 0.14, ly = tl.sy + (tr.sy - tl.sy) * 0.28 + (bl.sy - tl.sy) * 0.14;
      const sg2 = ctx.createRadialGradient(lx, ly, 0, lx, ly, Math.hypot(tr.sx - tl.sx, tr.sy - tl.sy) * 0.6);
      sg2.addColorStop(0, `rgba(255,250,240,${0.22 * light})`); sg2.addColorStop(1, 'transparent');
      ctx.save(); ctx.beginPath(); ctx.moveTo(tl.sx, tl.sy); ctx.lineTo(tr.sx, tr.sy); ctx.lineTo(br.sx, br.sy); ctx.lineTo(bl.sx, bl.sy); ctx.closePath(); ctx.clip(); ctx.fillStyle = sg2; ctx.fillRect(0, 0, DCW, DCH); ctx.restore();
    }
    { ctx.strokeStyle = rgba(band.glow, 0.35 * Math.max(0, nz)); ctx.lineWidth = 1.4; ctx.beginPath(); ctx.moveTo(tr.sx, tr.sy); ctx.lineTo(br.sx, br.sy); ctx.stroke(); }
    ctx.strokeStyle = M.emissive ? rgba(M.metDk, 0.5) : 'rgba(40,24,12,.34)'; ctx.lineWidth = 1.1;
    ctx.beginPath(); ctx.moveTo(tl.sx, tl.sy); ctx.lineTo(tr.sx, tr.sy); ctx.lineTo(br.sx, br.sy); ctx.lineTo(bl.sx, bl.sy); ctx.closePath(); ctx.stroke();
  });

  const tgN = ctx.createRadialGradient(apex.sx - 1, apex.sy - 1, 0, apex.sx, apex.sy, 5);
  tgN.addColorStop(0, M.metHi); tgN.addColorStop(1, M.metDk);
  if (M.emissive) { ctx.shadowColor = band.glow; ctx.shadowBlur = 10; }
  ctx.beginPath(); ctx.arc(apex.sx, apex.sy, 4, 0, Math.PI * 2); ctx.fillStyle = tgN; ctx.fill(); ctx.shadowBlur = 0;
  ctx.strokeStyle = 'rgba(255,255,255,.4)'; ctx.lineWidth = 0.7; ctx.stroke();

  if (capFront > 0) { drawLid(topV); drawPeg(); }

  // ── Spin-speed motion FX: rotating speed-arcs + ground dust streaks. Scaled by
  // spinSpeed so the energy eases off as the top slows (it never looks static). ──
  if (S.spinSpeed > 0.06) {
    const spd = S.spinSpeed;
    let bcx = 0, bcy = 0; topV.concat(botV).forEach((p) => { bcx += p.sx; bcy += p.sy; }); bcx /= (NF * 2); bcy /= (NF * 2);
    const ringR = DSC * 1.18;
    ctx.save();
    ctx.lineCap = 'round';
    // tangential motion arcs whipping around the body
    const arcN = 7;
    for (let i = 0; i < arcN; i++) {
      const a = now * (4 + spd * 16) + (i / arcN) * Math.PI * 2;
      const a2 = a + 0.5 + spd * 0.4;
      const rr = ringR * (0.9 + 0.12 * Math.sin(now * 3 + i));
      ctx.globalAlpha = 0.18 * spd;
      ctx.strokeStyle = rgba(band.glow, 0.9); ctx.lineWidth = 2.2;
      ctx.beginPath(); ctx.ellipse(bcx, bcy, rr, rr * 0.42, 0, a, a2); ctx.stroke();
    }
    // ground dust kicked off the tip as it travels the surface
    const dustY = apex.sy + 4;
    for (let i = 0; i < 5; i++) {
      const off = ((now * (160 + spd * 520) + i * 24) % 40);
      const dir = i % 2 ? 1 : -1;
      ctx.globalAlpha = Math.max(0, (0.4 - off / 60)) * spd;
      ctx.strokeStyle = rgba(band.glow, 0.8); ctx.lineWidth = 1.6;
      ctx.beginPath(); ctx.moveTo(apex.sx + dir * (6 + off), dustY); ctx.lineTo(apex.sx + dir * (16 + off), dustY + 2); ctx.stroke();
    }
    ctx.restore();
    ctx.globalAlpha = 1;
  }

  if (reveal >= 0 && S.showReveal) {
    const f = faces[reveal % NF];
    const label = f ? (lang === 'es' ? (f.nameTranslate || f.name) : f.name) : '';
    ctx.save();
    ctx.fillStyle = rgba(band.glow, 0.14);
    const bx = 40, by = DCH - 58, bw = DCW - 80, bh = 40, r = 12;
    ctx.beginPath(); ctx.moveTo(bx + r, by); ctx.arcTo(bx + bw, by, bx + bw, by + bh, r); ctx.arcTo(bx + bw, by + bh, bx, by + bh, r); ctx.arcTo(bx, by + bh, bx, by, r); ctx.arcTo(bx, by, bx + bw, by, r); ctx.closePath(); ctx.fill();
    ctx.strokeStyle = rgba(band.glow, 0.5); ctx.lineWidth = 1.2; ctx.stroke();
    ctx.fillStyle = M.emissive ? '#EAFBFF' : '#F4ECD8'; ctx.font = `800 16px 'DM Sans',sans-serif`; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(label, DCX, by + bh / 2 - 1);
    ctx.restore();
  }
}

const FRONT = Math.PI / 2; // face i is camera-front when sp = FRONT - (i+0.5)*DFARC
const easeOut = (t: number) => 1 - Math.pow(1 - t, 2.4);

export const Pirinola: React.FC<PirinolaProps> = ({
  prize,
  brand,
  faces = DEFAULT_FACES,
  edition = 'traditional',
  /*
   * SIN NOMBRE DE MARCA NO SE ROTULA NADA — igual que en Xinkit.
   *
   * El defecto era `'BRAND'`, y como el `config` casi nunca lo trae, la
   * superficie de juego enseñaba «BRAND» a quien respondía. Vacío es seguro:
   * `drawLogoFitted` sólo dibuja el texto cuando no hay logo, y `fillText('')`
   * no pinta nada — el borde, las marcas y los pétalos se dibujan aparte.
   */
  wordmark = '',
  language = 'en',
  reducedMotion = false,
  demoPhase,
  onRevealed,
}) => {
  const cvRef = useRef<HTMLCanvasElement>(null);
  const logoImg = useRef<HTMLImageElement | null>(null);
  const embers = useRef<Ember[]>([]);
  const revealedFired = useRef(false);
  const [, force] = useState(0);
  const [spun, setSpun] = useState(false);
  const [revealed, setRevealed] = useState(false); // drives the ARIA label after the win

  const band = useMemo(() => deriveBand(brand, edition, wordmark), [brand, edition, wordmark]);

  // Honor the prop OR the kiosk/OS "reduce motion" setting (game protocol §7).
  const prefersReducedMotion = useMemo(() => {
    if (reducedMotion) return true;
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    return false;
  }, [reducedMotion]);

  // The toton always has SIX faces. Pad by cycling (with the lowest-rated filler so
  // we never duplicate a jackpot) when fewer than 6 prizes are supplied.
  const faceSet = useMemo(() => {
    if (faces.length >= 6) return faces.slice(0, 6);
    if (faces.length === 0) return DEFAULT_FACES;
    const filler = faces.reduce((a, b) => ((b.rating ?? 3) <= (a.rating ?? 3) ? b : a), faces[0]);
    const out = faces.slice();
    while (out.length < 6) out.push(filler);
    return out;
  }, [faces]);

  // Which face the engine-chosen prize occupies.
  const targetIdx = useMemo(() => {
    const byId = faceSet.findIndex((f) => String(f.id) === String(prize.id));
    if (byId >= 0) return byId;
    const byRating = faceSet.findIndex((f) => (f.rating ?? 3) === (prize.rating ?? 3));
    return byRating >= 0 ? byRating : 0;
  }, [faceSet, prize]);

  // Physics state machine (ported from PiriGameCanvas).
  const st = useRef({
    mode: 'idle' as 'idle' | 'spin' | 'topple' | 'settle',
    sp: FRONT - 0.5 * DFARC, wob: 0, fall: 0, va: D_VA0, reveal: -1,
    target: 0, t0: 0, dur: 0, startSp: 0, total: 0,
    rest: { fall: 0.30, wob: 0.16, drift: 0.12, ph: 0 }, toppleT0: 0,
    travA: 0, tx: 0, ty: 0, restTx: 0, restTy: 0,  // surface travel (wander) state
  });

  useEffect(() => {
    embers.current = Array.from({ length: 15 }, () => ({
      x: Math.random() * DCW, y: Math.random() * DCH, vy: 0.12 + Math.random() * 0.32,
      r: 1 + Math.random() * 2.2, a: 0.1 + Math.random() * 0.22, ph: Math.random() * 6,
    }));
  }, []);

  // Preload the brand logo for the playing surface.
  useEffect(() => {
    if (!brand.logo) { logoImg.current = null; return; }
    const im = new Image();
    im.crossOrigin = 'anonymous';
    im.onload = () => { logoImg.current = im; force((n) => n + 1); };
    im.src = brand.logo;
    return () => { im.onload = null; };
  }, [brand.logo]);

  const baseState = useCallback((over: Partial<RenderState>): RenderState => ({
    sp: 0, wob: 0, fall: 0, va: D_VA0, reveal: -1, now: 0, spinSpeed: 0, tx: 0, ty: 0,
    edition, brand, band, lang: language, embers: embers.current, showReveal: false,
    faces: faceSet, logoImg: logoImg.current, wordmark, ...over,
  }), [edition, brand, band, language, faceSet, wordmark]);

  // Trigger a spin toward the pre-resolved target face.
  const spin = useCallback(() => {
    const s = st.current;
    if (s.mode !== 'idle' || spun) return;
    setSpun(true);
    revealedFired.current = false;
    const rs = Math.random() < 0.5 ? -1 : 1;
    s.target = targetIdx;
    s.rest = {
      fall: 0.40 + Math.random() * 0.08,  // deeper topple — clearly fallen/landed
      wob: 0.14 + Math.random() * 0.05,
      drift: rs * (0.10 + Math.random() * 0.06),
      ph: Math.random() * Math.PI * 2,
    };
    // Final resting place — slightly OFF-CENTER on the disc (never dead center).
    const restAngle = Math.random() * Math.PI * 2;
    s.restTx = Math.cos(restAngle) * 24;
    s.restTy = Math.sin(restAngle) * 9;
    if (prefersReducedMotion) {
      s.sp = FRONT - (s.target + 0.5) * DFARC + s.rest.drift;
      s.fall = s.rest.fall; s.wob = s.rest.wob; s.va = D_VA0 + 0.14;
      s.tx = s.restTx; s.ty = s.restTy;
      s.reveal = s.target; s.mode = 'settle'; s.toppleT0 = performance.now() / 1000;
      return;
    }
    const targetRot = FRONT - (s.target + 0.5) * DFARC;
    const tN = ((targetRot % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    const cN = ((s.sp % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    let delta = (tN - cN + Math.PI * 2) % (Math.PI * 2); if (delta < 0.4) delta += Math.PI * 2;
    s.startSp = s.sp;
    s.total = (7 + Math.random() * 4) * (Math.PI * 2) + delta;
    s.dur = 3.7 + Math.random() * 1.2;
    s.t0 = performance.now() / 1000;
    s.mode = 'spin';
  }, [targetIdx, prefersReducedMotion, spun]);

  // ── Static demoPhase poses (Storybook states) ──
  useEffect(() => {
    if (!demoPhase) {
      // Re-arm for a fresh interactive run (mirrors SoccerBall's setup effect).
      st.current.mode = 'idle';
      revealedFired.current = false;
      setSpun(false); setRevealed(false);
      return;
    }
    revealedFired.current = true; // demo never fires onRevealed
  }, [demoPhase]);

  // ── Animation loop ──
  useEffect(() => {
    let id: number; let alive = true;
    const loop = (nowMs: number) => {
      if (!alive) return;
      const now = nowMs / 1000;
      const cv = cvRef.current;
      const ctx = cv?.getContext('2d');
      if (!ctx) { id = requestAnimationFrame(loop); return; }

      if (demoPhase) {
        ctx && renderDemo(ctx, demoPhase, now);
        id = requestAnimationFrame(loop);
        return;
      }

      const s = st.current;
      let reveal = -1, spinSpeed = 0;

      if (s.mode === 'spin') {
        const t = Math.min((now - s.t0) / s.dur, 1);
        const e = easeOut(t);
        s.sp = s.startSp + s.total * e;
        const speed = (1 - t) * s.total / s.dur * 0.016;
        spinSpeed = Math.min(1, speed / 0.32);

        // TRAVEL: the top wanders the surface — spirals out as it gets going,
        // then spirals inward and parks at its off-center rest as it slows.
        s.travA += 0.05 + spinSpeed * 0.34;
        const ramp = Math.min(1, t / 0.12);          // quick spin-up
        const ampF = ramp * Math.pow(1 - e, 0.7);    // wide early, fades to 0 at the end
        s.tx = Math.cos(s.travA) * 44 * ampF + s.restTx * (1 - ampF);
        s.ty = Math.sin(s.travA) * 15 * ampF + s.restTy * (1 - ampF);

        // PRECESSION: as it slows it leans outward (along travel) and starts tipping
        // toward the viewer — "hunting for its axis" before the topple.
        const lean = 0.03 + 0.17 * (1 - spinSpeed);
        s.wob = Math.cos(s.travA) * lean;
        s.fall = (1 - spinSpeed) * 0.07;
        s.va = D_VA0 + (1 - spinSpeed) * 0.05;

        if (t >= 1) { s.sp = s.startSp + s.total; s.mode = 'topple'; s.toppleT0 = now; }
      } else if (s.mode === 'topple') {
        const TT = 1.05;
        const tt = Math.min((now - s.toppleT0) / TT, 1);
        const p = easeOut(tt);
        s.fall = s.rest.fall * p;
        s.va = D_VA0 + 0.14 * p;
        const grow = Math.sin(tt * Math.PI * 3.2 + s.rest.ph) * (0.34 * (1 - Math.abs(0.5 - tt) * 0.6));
        s.wob = grow * (1 - tt * 0.4) + s.rest.wob * p;
        s.sp = (FRONT - (s.target + 0.5) * DFARC) + s.rest.drift * p + Math.sin(tt * 6 + s.rest.ph) * 0.03 * (1 - tt);
        // residual circular wobble of the contact point, damping to the rest spot
        s.travA += 0.05 * (1 - tt);
        s.tx = s.restTx + Math.cos(s.travA) * 7 * (1 - tt);
        s.ty = s.restTy + Math.sin(s.travA) * 3 * (1 - tt);
        reveal = tt > 0.55 ? s.target : -1;
        if (tt >= 1) { s.mode = 'settle'; s.toppleT0 = now; }
      } else if (s.mode === 'settle') {
        const u = now - s.toppleT0;
        const damp = Math.exp(-u * 1.4);
        s.fall = s.rest.fall + Math.sin(u * 4.6 + s.rest.ph) * 0.05 * damp;
        s.wob = s.rest.wob + Math.sin(u * 5.2 + s.rest.ph) * 0.07 * damp + Math.sin(u * 1.6) * 0.012;
        s.sp = (FRONT - (s.target + 0.5) * DFARC) + s.rest.drift + Math.sin(u * 1.1 + s.rest.ph) * 0.012;
        s.va = D_VA0 + 0.14;
        s.tx = s.restTx + Math.sin(u * 5 + s.rest.ph) * 1.5 * damp;
        s.ty = s.restTy + Math.cos(u * 5 + s.rest.ph) * 0.8 * damp;
        reveal = s.target;
        // Dwell on the landed, glowing winning face so the reveal reads before the win screen.
        if (u > 1.5 && !revealedFired.current) {
          revealedFired.current = true;
          setRevealed(true);
          onRevealed?.(prize);
        }
      } else { // idle — upright on its tip at center, slow breathing spin
        s.sp += 0.006;
        s.wob = Math.sin(now * 1.1) * 0.05;
        s.fall = 0; s.va = D_VA0; s.tx = 0; s.ty = Math.sin(now * 1.6) * 1.2;
      }

      renderPirinola(ctx, baseState({ sp: s.sp, wob: s.wob, fall: s.fall, va: s.va, reveal, now, spinSpeed, tx: s.tx, ty: s.ty }));
      id = requestAnimationFrame(loop);
    };

    // Render a fixed, inspectable pose for each Storybook state.
    function renderDemo(ctx: CanvasRenderingContext2D, phase: PirinolaPhase, now: number) {
      const rest = FRONT - (targetIdx + 0.5) * DFARC;
      if (phase === 'idle') {
        renderPirinola(ctx, baseState({ sp: now * 0.5, wob: Math.sin(now * 1.1) * 0.05, fall: 0, va: D_VA0, reveal: -1, now, ty: Math.sin(now * 1.6) * 1.2 }));
      } else if (phase === 'spinning') {
        // fast spin while travelling a wide orbit on the surface (full solid form, with point)
        const a = now * 3.2;
        renderPirinola(ctx, baseState({ sp: now * 16, wob: Math.cos(a) * 0.05, fall: 0.03, va: D_VA0 + 0.02, reveal: -1, now, spinSpeed: 1, tx: Math.cos(a) * 40, ty: Math.sin(a) * 13 }));
      } else if (phase === 'settling') {
        // precession: leaning + tipping toward the viewer, circling a tightening orbit
        const a = now * 2.0;
        renderPirinola(ctx, baseState({ sp: rest + 0.16 + Math.sin(now * 1.1) * 0.04, wob: Math.cos(a) * 0.24, fall: 0.16 + Math.sin(now * 1.35) * 0.03, va: D_VA0 + 0.06, reveal: -1, now, spinSpeed: 0.34, tx: Math.cos(a) * 18, ty: Math.sin(a) * 6 }));
      } else { // resolution + winReveal — clearly LANDED off-center pose, winning face glowing
        const w = 0.18 + Math.sin(now * 1.6) * 0.018;
        const fl = 0.42 + Math.sin(now * 1.3) * 0.012;
        const sp = rest + 0.12;
        renderPirinola(ctx, baseState({ sp, wob: w, fall: fl, va: D_VA0 + 0.14, reveal: targetIdx, now, showReveal: phase === 'winReveal', tx: 18, ty: 6 }));
      }
    }

    id = requestAnimationFrame(loop); // single rAF chain (loop reschedules itself)
    return () => { alive = false; cancelAnimationFrame(id); };
  }, [demoPhase, baseState, targetIdx, onRevealed, prize]);

  const lightBg = isLightBg(brand.font);
  const fgMuted = rgba(brand.font, 0.58);
  const prizeName = language === 'es' ? (prize?.nameTranslate || prize?.name) : prize?.name;

  const showHint = !spun && (!demoPhase || demoPhase === 'idle');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
      <style>{'@keyframes piriHintPulse{0%,100%{opacity:.45}50%{opacity:1}}'}</style>
      <div style={{ position: 'relative', width: '100%', maxWidth: DCW }}>
        <canvas
          ref={cvRef}
          width={DCW}
          height={DCH}
          role="img"
          aria-label={
            revealed && !demoPhase
              ? `${T.won[language]} ${prizeName}`
              : `${T.hint[language]}.`
          }
          onClick={() => { if (!demoPhase) spin(); }}
          style={{
            display: 'block', width: '100%', maxWidth: DCW, borderRadius: 18,
            cursor: demoPhase || spun ? 'default' : 'pointer',
            WebkitTapHighlightColor: 'transparent', userSelect: 'none',
            boxShadow: `0 12px 40px ${rgba(darken(brand.primary, 0.4), 0.5)}`,
          }}
        />
        {/* Pulsing "tap the top" hint over the toton while idle */}
        {showHint && (
          <div
            aria-hidden
            style={{
              position: 'absolute', left: 0, right: 0, bottom: '8%', textAlign: 'center',
              fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 700, letterSpacing: '0.03em',
              color: band.glow, textShadow: '0 1px 6px rgba(0,0,0,0.6)', pointerEvents: 'none',
              animation: 'piriHintPulse 1.6s ease-in-out infinite',
            }}
          >
            {T.hint[language]}
          </div>
        )}
      </div>

      {/* Single tap-to-spin trigger (also the non-gesture/keyboard fallback) */}
      {!demoPhase && (
        <button
          type="button"
          onClick={spin}
          disabled={spun}
          style={{
            border: 'none', color: readableOn(band.glow),
            background: spun ? rgba(band.glow, 0.4) : band.glow,
            borderRadius: 999, padding: '12px 30px', fontSize: 15, fontWeight: 800, letterSpacing: '0.04em',
            cursor: spun ? 'default' : 'pointer', fontFamily: "'DM Sans', sans-serif",
            opacity: spun ? 0.6 : 1, boxShadow: `0 6px 20px ${rgba(band.glow, 0.4)}`,
          }}
        >
          {T.spin[language]}
        </button>
      )}

      {/* Powered by Xotski — 8px, bottom-center, non-configurable */}
      <div aria-hidden style={{ fontSize: 8, letterSpacing: 0.5, color: lightBg ? 'rgba(0,0,0,0.55)' : fgMuted }}>
        Powered by Xotski
      </div>
    </div>
  );
};

export default Pirinola;
